import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { IUiState } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { debounceTime, filter, map, mergeMap } from 'rxjs';

import { IBetslipRootState } from '../../base/store/state';
import { BetslipType } from '../../core/betslip-type';
import { BetslipPlaceBetActions } from '../betplacement/actions';
import { BetslipOverAskActions } from '../over-ask/actions';
import BetslipPickActions from '../picks/actions';
import { betslipSelectedTypeStateSelector } from '../types/selectors';
import BetslipErrorsActions from '../validation/actions';
import { BetslipError } from '../validation/errors/betslip-error';
import { BetBuilderBetslipNotification, IBetslipNotification, OveraskBetslipNotification } from '../validation/errors/betslip-notification';
import { isNotifyUserChangeError } from '../validation/services/utils/betslip-errors-utils';
import { BetslipNotificationActions } from './betslip-notification.actions';
import { selectNotificationErrorState } from './selectors';
import { BetslipNotificationConverter } from './services/betslip-notification-converter';
import { BetslipNotificationUtils } from './services/betslip-notification.utils';

@Injectable()
export class BetslipNotificationEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private betslipNotificationUtils: BetslipNotificationUtils,
        private betslipConfig: BetslipConfig,
    ) {}

    createShowNotificationMessageEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipErrorsActions.picksErrorsCheckFinished,
                BetslipErrorsActions.betslipErrorsCheckFinished,
                BetslipErrorsActions.addPickError,
                BetslipErrorsActions.addBetslipError,
                BetslipErrorsActions.placeBetResponseErrors,
            ),
            concatLatestFrom(() => this.store.select(selectNotificationErrorState)),
            mergeMap(([action, { notifications, ui, errors }]) => {
                const notifyUserErrors = errors.filter((value) => isNotifyUserChangeError(value) && !value.userHasBeenNotified);

                const currentNotification = BetslipNotificationConverter.fromState(notifications);
                const notificationAction = this.getNotificationAction(notifyUserErrors, currentNotification, ui);

                return notificationAction ? [notificationAction] : [];
            }),
        );
    });

    markPickErrorsSeenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipNotificationActions.clearMessage),
            map(() => BetslipErrorsActions.markAllNotifyUserErrorsAsSeen()),
        );
    });

    clearMessage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipErrorsActions.acceptBetslipChanges,
                BetslipErrorsActions.acceptBetslipEditBetChanges,
                BetslipPlaceBetActions.requestPlaceBet,
                BetslipOverAskActions.startFlow,
                BetslipPickActions.addPick,
                BetslipPickActions.removePick,
                BetslipPickActions.removeMultiplePicks,
            ),
            map((_) => BetslipNotificationActions.clearMessage()),
        );
    });

    onOddsChange$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipNotificationActions.setMessage),
            concatLatestFrom(() => this.store.select(betslipSelectedTypeStateSelector)),
            filter(
                ([action, selectedType]) =>
                    !!selectedType && selectedType !== BetslipType.EditBet && !(action.payload instanceof OveraskBetslipNotification),
            ),
            debounceTime(this.betslipConfig.betslipToasterVisibilityTimespan),
            map((_) => BetslipNotificationActions.clearMessage()),
        );
    });

    private getNotificationAction(errors: BetslipError[], currentNotification: Nullable<IBetslipNotification>, uiState: IUiState): Action | null {
        if (errors.length > 0 || currentNotification instanceof OveraskBetslipNotification) {
            const toastNotification = this.betslipNotificationUtils.getToastNotification(errors, uiState);
            if (!toastNotification.isEqual(currentNotification)) {
                return BetslipNotificationActions.setMessage({ payload: toastNotification });
            }
        } else if (currentNotification && !(currentNotification instanceof BetBuilderBetslipNotification)) {
            return BetslipNotificationActions.clearMessage();
        }

        return null;
    }
}
